import { fetchAndOpenModal } from "@apps/rewards/utils/modal/utils";
import { subscribeToSdkMethod } from "@modules/sdk/helpers";
import { lastValueFrom, tap } from "rxjs";

import { REWARDS_SDK_METHODS } from "../constants";
import type GetCustomerActiveRewardsSdkMethodPayload from "../methods/GetCustomerActiveRewardsSdkMethodPayload";
import type GetRewardsEarningRulesSdkMethodPayload from "../methods/GetRewardsEarningRulesSdkMethodPayload";
import type GetRewardsProgramAccountSdkMethodPayload from "../methods/GetRewardsProgramAccountSdkMethodPayload";
import type GetRewardsSpendingRulesSdkMethodPayload from "../methods/GetRewardsSpendingRulesSdkMethodPayload";
import type GetRewardsVipTierProgressSdkMethodPayload from "../methods/GetRewardsVipTierProgressSdkMethodPayload";
import type GetRewardsVipTiersSdkMethodPayload from "../methods/GetRewardsVipTiersSdkMethodPayload";
import type OpenRewardsModalSdkMethodPayload from "../methods/OpenRewardsModalSdkMethodPayload";
import type RedeemRewardSdkMethodPayload from "../methods/RedeemRewardSdkMethodPayload";
import SendVerificationTokenSdkMethodPayload from "../methods/SendVerificationTokenSdkMethodPayload";
import type { SubscribeToSdkMethodsParams } from "../types";
import { fetchProduct } from "./generalUtils";

export const subscribeToSdkMethods = ({
    programAccountApiService,
    earningRulesApiService,
    spendingRulesApiService,
    customerRewardsApiService,
    vipTiersApiService,
    modalManager,
    dynamicImportManager,
    productsService,
    verificationTokenApiService,
}: SubscribeToSdkMethodsParams): void => {
    subscribeToSdkMethod<GetRewardsProgramAccountSdkMethodPayload>(
        REWARDS_SDK_METHODS.GET_REWARDS_PROGRAM_ACCOUNT,
        ({ locale, onSuccess, onError }) => {
            lastValueFrom(
                programAccountApiService.getRewardsProgramAccount({
                    locale,
                })
            )
                .then((result) => onSuccess(result))
                .catch((error: Error) => onError(error));
        }
    );

    subscribeToSdkMethod<GetRewardsEarningRulesSdkMethodPayload>(
        REWARDS_SDK_METHODS.GET_REWARDS_EARNING_RULES,
        ({ locale, onSuccess, onError }) => {
            lastValueFrom(
                earningRulesApiService
                    .getEarningRules({ locale })
                    .pipe(
                        tap(({ rules }) =>
                            rules.forEach(({ rewarding }) =>
                                fetchProduct(rewarding, productsService)
                            )
                        )
                    )
            )
                .then((result) => onSuccess(result))
                .catch((error: Error) => onError(error));
        }
    );

    subscribeToSdkMethod<GetRewardsSpendingRulesSdkMethodPayload>(
        REWARDS_SDK_METHODS.GET_REWARDS_SPENDING_RULES,
        ({ locale, onSuccess, onError }) => {
            lastValueFrom(
                spendingRulesApiService.getSpendingRules({ locale }).pipe(
                    tap(({ items }) =>
                        items.forEach(({ metadata: { products } }) => {
                            if (products.length) {
                                productsService.addProductForFetching(
                                    products[0].id
                                );
                            }
                        })
                    )
                )
            )
                .then((result) => onSuccess(result))
                .catch((error: Error) => onError(error));
        }
    );

    subscribeToSdkMethod<RedeemRewardSdkMethodPayload>(
        REWARDS_SDK_METHODS.REDEEM_REWARD,
        ({ body, onSuccess, onError }) => {
            lastValueFrom(spendingRulesApiService.redeem(body))
                .then(() => onSuccess())
                .catch((error: Error) => onError(error));
        }
    );

    subscribeToSdkMethod<GetCustomerActiveRewardsSdkMethodPayload>(
        REWARDS_SDK_METHODS.GET_CUSTOMER_ACTIVE_REWARDS,
        ({ locale, onSuccess, onError }) => {
            lastValueFrom(
                customerRewardsApiService
                    .getCustomerActiveRewards({
                        locale,
                    })
                    .pipe(
                        tap(({ rewards }) =>
                            rewards.forEach(({ reward }) =>
                                fetchProduct(reward, productsService)
                            )
                        )
                    )
            )
                .then((result) => onSuccess(result))
                .catch((error: Error) => onError(error));
        }
    );

    subscribeToSdkMethod<GetRewardsVipTiersSdkMethodPayload>(
        REWARDS_SDK_METHODS.GET_REWARDS_VIP_TIERS,
        ({ locale, onSuccess, onError }) => {
            lastValueFrom(
                vipTiersApiService
                    .getTiers({ locale })
                    .pipe(
                        tap(({ tiers }) =>
                            tiers.forEach(({ rewarding }) =>
                                fetchProduct(rewarding, productsService)
                            )
                        )
                    )
            )
                .then((result) => onSuccess(result))
                .catch((error: Error) => onError(error));
        }
    );

    subscribeToSdkMethod<GetRewardsVipTierProgressSdkMethodPayload>(
        REWARDS_SDK_METHODS.GET_REWARDS_VIP_TIER_PROGRESS,
        ({ onSuccess, onError }) => {
            lastValueFrom(vipTiersApiService.getTierProgress())
                .then((result) => onSuccess(result))
                .catch((error: Error) => onError(error));
        }
    );

    subscribeToSdkMethod<OpenRewardsModalSdkMethodPayload>(
        REWARDS_SDK_METHODS.OPEN_REWARDS_MODAL,
        ({ modalName, params, onSuccess, onError }) => {
            fetchAndOpenModal({
                modalName,
                modalManager,
                dynamicImportManager,
                params,
            })
                .then(() => onSuccess(params))
                .catch((error: Error) => onError(error));
        }
    );

    subscribeToSdkMethod<SendVerificationTokenSdkMethodPayload>(
        REWARDS_SDK_METHODS.SEND_VERIFICATION_TOKEN,
        ({ onSuccess, onError }) => {
            lastValueFrom(verificationTokenApiService.sendVerificationToken())
                .then(() => onSuccess())
                .catch((error: Error) => onError(error));
        }
    );
};
